import {
  PreferenceTagItemModel,
  getPreferenceGroupsKey,
  useGetPreferenceGroups,
} from 'api-hooks/preference-tag';
import { OnChangeParams, Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { Input } from 'components/elements';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useMultiSelectInputHelper from 'hooks/use-multi-select-input-helper';
import useNavigation from 'hooks/use-navigation';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  noMargin?: boolean;
  type?: string;
  fieldType?: 'control' | 'uncontrol';
}

function transformer(item: PreferenceTagItemModel): Option {
  return {
    id: item.id,
    label: item.name,
  };
}

export default function PreferenceMultiInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:preference'),
    placeholder = t('common:select_preference'),
    type,
  } = props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });
  const permission = useGetCrudPermission('preference-groups');
  const { navigate } = useNavigation();
  const { overrides, ...selectProps } = useMultiSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value || [], {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetPreferenceGroups,
    listKeyFunction: getPreferenceGroupsKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          type,
        },
      }),
      [type],
    ),
    renderCreate: permission.create,
    onClickCreate() {
      navigate(NavigationRoutes.PreferenceTagsCreate);
      props.onClose?.();
    },
    listTransformer: React.useCallback((result) => {
      return (result.data || []).map((v) => transformer(v));
    }, []),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      fieldType={props.fieldType}
      multi
      {...selectProps}
    />
  );
}
